/**
 * Convex-native error handling system for VRP System v4
 * Implements structured error handling with ConvexError patterns
 * Phase 5: Error Handling & Debugging implementation
 */

import { ConvexError } from 'convex/values'

// =============================================================================
// CONVEX ERROR TYPES AND INTERFACES
// =============================================================================

export interface ConvexErrorDetails {
  code: string
  message: string
  data?: any
  requestId?: string
  timestamp?: number
  context?: string
  userId?: string
  retryable?: boolean
  [key: string]: any // Index signature to satisfy Convex Value constraint
}

export interface ErrorHandlingOptions {
  context?: string
  fallbackMessage?: string
  includeRequestId?: boolean
  logLevel?: 'error' | 'warn' | 'info' | 'debug'
  requestId?: string
}

// =============================================================================
// CONVEX ERROR PATTERNS
// =============================================================================

/**
 * Parse ConvexError and structured errors for user-friendly display
 * Supports Convex's native error patterns with request ID tracking
 */
export function parseConvexError(
  error: unknown,
  options: ErrorHandlingOptions = {}
): string {
  const {
    context = 'operation',
    fallbackMessage = `Failed to complete ${context}`,
    includeRequestId = false,
    requestId,
  } = options

  // Handle null/undefined errors
  if (!error) {
    return fallbackMessage
  }

  // Handle ConvexError objects (native Convex errors)
  if (error instanceof ConvexError) {
    const errorData = error.data as ConvexErrorDetails
    let message = errorData?.message || error.message

    // Add request ID if available and requested
    if (includeRequestId && (errorData?.requestId || requestId)) {
      message += ` (Request ID: ${errorData?.requestId || requestId})`
    }

    return message
  }

  // Handle Error objects with ConvexError in message
  if (error instanceof Error) {
    const message = error.message.toLowerCase()

    // Extract ConvexError details if present
    if (error.message.includes('ConvexError:')) {
      const convexErrorMatch = error.message.match(/ConvexError: (.+)/)
      if (convexErrorMatch) {
        return convexErrorMatch[1]
      }
    }

    // Authentication-specific errors
    if (
      message.includes('invalid login credentials') ||
      message.includes('user not found') ||
      message.includes('unauthorized')
    ) {
      return 'Invalid email or password. Please check your credentials and try again.'
    }

    if (
      message.includes('email already exists') ||
      message.includes('already registered')
    ) {
      return 'This email is already registered. Try signing in instead.'
    }

    if (
      message.includes('permission denied') ||
      message.includes('access denied')
    ) {
      return 'Permission denied. Please sign in again.'
    }

    // Network and server errors
    if (
      message.includes('network') ||
      message.includes('fetch failed') ||
      message.includes('connection')
    ) {
      return 'Network error. Please check your connection and try again.'
    }

    if (message.includes('timeout') || message.includes('timed out')) {
      return 'Request timed out. Please try again.'
    }

    if (
      message.includes('server error') ||
      message.includes('internal server')
    ) {
      return 'Server error. Please try again in a few moments.'
    }

    if (message.includes('not found')) {
      return `${context} not found. It may have been deleted or moved.`
    }

    // Rate limiting
    if (
      message.includes('rate limit') ||
      message.includes('too many requests')
    ) {
      return 'Too many requests. Please wait a moment before trying again.'
    }

    // Validation errors
    if (message.includes('validation') || message.includes('invalid')) {
      return isUserFriendlyMessage(error.message)
        ? error.message
        : 'Invalid input. Please check your data and try again.'
    }

    // Return original message if it's user-friendly
    if (isUserFriendlyMessage(error.message)) {
      return error.message
    }
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error
  }

  // Handle structured error objects
  if (typeof error === 'object' && error !== null) {
    const errorObj = error as any

    // Check for ConvexError structure
    if (errorObj.code && errorObj.message) {
      return errorObj.message
    }

    if (errorObj.message) {
      return parseConvexError(errorObj.message, options)
    }

    if (errorObj.error) {
      return parseConvexError(errorObj.error, options)
    }

    if (
      errorObj.errors &&
      Array.isArray(errorObj.errors) &&
      errorObj.errors.length > 0
    ) {
      return errorObj.errors[0].message || errorObj.errors[0]
    }
  }

  return fallbackMessage
}

/**
 * Check if an error message is user-friendly (doesn't contain technical details)
 */
function isUserFriendlyMessage(message: string): boolean {
  const technicalTerms = [
    'stack trace',
    'internal error',
    'undefined',
    'null',
    'function',
    'object',
    'promise',
    'async',
    'await',
    'throw',
    'catch',
    'try',
    'debug',
    'trace',
    'convex',
  ]

  const lowerMessage = message.toLowerCase()
  return !technicalTerms.some(term => lowerMessage.includes(term))
}

// Backward compatibility - keep old function name
export const parseErrorMessage = parseConvexError

// =============================================================================
// CONVEX ERROR CODES AND CONTEXTS
// =============================================================================

/**
 * Standardized error codes for Convex error handling
 */
export const ConvexErrorCodes = {
  // Authentication errors
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  EMAIL_ALREADY_EXISTS: 'EMAIL_ALREADY_EXISTS',

  // Permission errors
  ACCESS_DENIED: 'ACCESS_DENIED',
  OWNERSHIP_REQUIRED: 'OWNERSHIP_REQUIRED',
  PERMISSION_DENIED: 'PERMISSION_DENIED',

  // Resource errors
  NOT_FOUND: 'NOT_FOUND',
  ALREADY_EXISTS: 'ALREADY_EXISTS',
  RESOURCE_CONFLICT: 'RESOURCE_CONFLICT',

  // Validation errors
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  INVALID_INPUT: 'INVALID_INPUT',
  CONSTRAINT_VIOLATION: 'CONSTRAINT_VIOLATION',

  // System errors
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  TIMEOUT: 'TIMEOUT',

  // Network errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  CONNECTION_ERROR: 'CONNECTION_ERROR',

  // VRP-specific errors
  GEOCODING_ERROR: 'GEOCODING_ERROR',
  OPTIMIZATION_ERROR: 'OPTIMIZATION_ERROR',
  DATA_IMPORT_ERROR: 'DATA_IMPORT_ERROR',
} as const

/**
 * Context-specific error messages for operations
 */
export const ErrorContexts = {
  AUTHENTICATION: {
    SIGN_IN: 'sign in',
    SIGN_UP: 'create account',
    SIGN_OUT: 'sign out',
    RESET_PASSWORD: 'reset password',
  },
  PROJECT: {
    CREATE: 'create project',
    UPDATE: 'update project',
    DELETE: 'delete project',
    LOAD: 'load project',
  },
  SCENARIO: {
    CREATE: 'create scenario',
    UPDATE: 'update scenario',
    DELETE: 'delete scenario',
    LOAD: 'load scenario',
  },
  DATASET: {
    CREATE: 'create dataset',
    UPDATE: 'update dataset',
    DELETE: 'delete dataset',
    LOAD: 'load dataset',
    IMPORT: 'import data',
    EXPORT: 'export data',
  },
  TABLE: {
    CREATE: 'create row',
    UPDATE: 'update row',
    DELETE: 'delete row',
    LOAD: 'load table data',
    BULK_EDIT: 'bulk edit',
    BULK_DELETE: 'bulk delete',
  },
  GEOCODING: {
    FORWARD: 'geocode address',
    REVERSE: 'reverse geocode',
    VALIDATE: 'validate coordinates',
    BATCH: 'batch geocode',
  },
} as const

// =============================================================================
// CONVEX REQUEST ID TRACKING AND STRUCTURED LOGGING
// =============================================================================

/**
 * Generate a unique request ID for tracking operations
 */
export function generateRequestId(): string {
  return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Enhanced logging with Convex patterns and request ID tracking
 */
export function logConvexError(
  error: unknown,
  context: string,
  options: {
    requestId?: string
    userId?: string
    additionalInfo?: any
    level?: 'error' | 'warn' | 'info' | 'debug'
  } = {}
) {
  const { requestId, userId, additionalInfo, level = 'error' } = options
  const timestamp = new Date().toISOString()

  // Create structured log entry
  const logEntry = {
    timestamp,
    level,
    context,
    requestId,
    userId,
    error: {
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      code: error instanceof ConvexError ? error.data?.code : undefined,
    },
    additionalInfo,
  }

  // Console logging with enhanced formatting
  const logPrefix = `[${timestamp}] [${level.toUpperCase()}] [${context}]`
  const logSuffix = requestId ? ` (Request ID: ${requestId})` : ''

  console[level](`${logPrefix} Error:${logSuffix}`, error)

  if (additionalInfo) {
    console[level](`${logPrefix} Additional info:${logSuffix}`, additionalInfo)
  }

  if (userId) {
    console[level](`${logPrefix} User ID:${logSuffix}`, userId)
  }

  // In development, also log stack trace and structured data
  if (
    process.env.NODE_ENV === 'development' &&
    error instanceof Error &&
    error.stack
  ) {
    console[level](`${logPrefix} Stack trace:${logSuffix}`, error.stack)
    console[level](`${logPrefix} Structured log:${logSuffix}`, logEntry)
  }

  // Store in global error tracker for debugging (development only)
  if (process.env.NODE_ENV === 'development') {
    if (!(window as any).__convexErrorLogs) {
      ;(window as any).__convexErrorLogs = []
    }
    ;(window as any).__convexErrorLogs.push(logEntry)

    // Keep only last 100 entries
    if ((window as any).__convexErrorLogs.length > 100) {
      ;(window as any).__convexErrorLogs = (
        window as any
      ).__convexErrorLogs.slice(-100)
    }
  }
}

// Backward compatibility - keep old function name
export const logError = logConvexError

/**
 * Enhanced retry wrapper with Convex error patterns and request ID tracking
 */
export async function retryConvexOperation<T>(
  operation: () => Promise<T>,
  options: {
    maxRetries?: number
    baseDelay?: number
    context?: string
    requestId?: string
    userId?: string
    isRetryable?: (error: unknown) => boolean
  } = {}
): Promise<T> {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    context = 'operation',
    requestId = generateRequestId(),
    userId,
    isRetryable = defaultIsRetryable,
  } = options

  let lastError: unknown

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation()
    } catch (error) {
      lastError = error

      // Check if error is retryable
      if (!isRetryable(error)) {
        logConvexError(error, context, {
          requestId,
          userId,
          additionalInfo: { attempt, maxRetries, retryable: false },
        })
        throw error
      }

      // If this was the last attempt, throw the error
      if (attempt === maxRetries) {
        logConvexError(error, `${context} (final attempt)`, {
          requestId,
          userId,
          additionalInfo: { attempt, maxRetries, finalAttempt: true },
        })
        throw error
      }

      // Wait before retrying with exponential backoff + jitter
      const delay = baseDelay * Math.pow(2, attempt - 1)
      const jitter = Math.random() * 0.1 * delay // Add up to 10% jitter
      const finalDelay = Math.floor(delay + jitter)

      logConvexError(
        error,
        `${context} (attempt ${attempt}/${maxRetries}), retrying in ${finalDelay}ms`,
        {
          requestId,
          userId,
          additionalInfo: { attempt, maxRetries, retryDelay: finalDelay },
          level: 'warn',
        }
      )

      await new Promise(resolve => setTimeout(resolve, finalDelay))
    }
  }

  throw lastError
}

/**
 * Default retryable error check for Convex operations
 */
function defaultIsRetryable(error: unknown): boolean {
  // ConvexError with retryable flag
  if (error instanceof ConvexError) {
    const errorData = error.data as ConvexErrorDetails
    return errorData?.retryable === true
  }

  // Standard Error checking
  if (error instanceof Error) {
    const message = error.message.toLowerCase()

    // Don't retry these error types
    if (
      message.includes('unauthorized') ||
      message.includes('permission denied') ||
      message.includes('access denied') ||
      message.includes('not found') ||
      message.includes('validation') ||
      message.includes('invalid') ||
      message.includes('already exists')
    ) {
      return false
    }

    // Retry these error types
    if (
      message.includes('network') ||
      message.includes('timeout') ||
      message.includes('connection') ||
      message.includes('server error') ||
      message.includes('service unavailable') ||
      message.includes('rate limit')
    ) {
      return true
    }
  }

  // Default to not retryable for safety
  return false
}

// Backward compatibility - keep old function name
export const retryOperation = retryConvexOperation

// =============================================================================
// CONVEX ERROR HANDLING PATTERNS
// =============================================================================

/**
 * Create a ConvexError with structured data and request ID
 */
export function createConvexError(
  code: string,
  message: string,
  data?: any,
  requestId?: string
): ConvexError<ConvexErrorDetails> {
  const errorDetails = {
    code,
    message,
    data,
    requestId: requestId || generateRequestId(),
    timestamp: Date.now(),
    retryable: false,
  }

  return new ConvexError(errorDetails)
}

/**
 * Enhanced error handling patterns for authentication with ConvexError support
 */
export const ConvexAuthErrorHandling = {
  signIn: (error: unknown, requestId?: string) => {
    const message = parseConvexError(error, {
      context: ErrorContexts.AUTHENTICATION.SIGN_IN,
      fallbackMessage: 'Failed to sign in. Please try again.',
      requestId,
    })

    logConvexError(error, 'Authentication.SignIn', {
      requestId,
      additionalInfo: { operation: 'signIn' },
    })

    return message
  },

  signUp: (error: unknown, requestId?: string) => {
    const message = parseConvexError(error, {
      context: ErrorContexts.AUTHENTICATION.SIGN_UP,
      fallbackMessage: 'Failed to create account. Please try again.',
      requestId,
    })

    logConvexError(error, 'Authentication.SignUp', {
      requestId,
      additionalInfo: { operation: 'signUp' },
    })

    return message
  },

  signOut: (error: unknown, requestId?: string) => {
    const message = parseConvexError(error, {
      context: ErrorContexts.AUTHENTICATION.SIGN_OUT,
      fallbackMessage: 'Failed to sign out. Please try again.',
      requestId,
    })

    logConvexError(error, 'Authentication.SignOut', {
      requestId,
      additionalInfo: { operation: 'signOut' },
      level: 'warn', // Sign out errors are usually less critical
    })

    return message
  },
}

// Backward compatibility - keep old name
export const AuthErrorHandling = ConvexAuthErrorHandling

// =============================================================================
// GEOCODING TYPES AND INTERFACES
// =============================================================================

export interface GeocodingResult {
  coordinates: [number, number] // [longitude, latitude]
  address: string
  confidence: 'exact' | 'interpolated' | 'approximate'
  components: {
    street?: string
    city?: string
    state?: string
    country?: string
    postalCode?: string
  }
}

export interface AddressResult {
  address: string
  components: GeocodingResult['components']
}

// =============================================================================
// GEOCODING SERVICE
// =============================================================================

/**
 * Mapbox Geocoding Service integrated with error handling
 */
export class MapboxGeocodingService {
  private apiKey: string
  private cache = new Map<string, { result: any; timestamp: number }>()
  private baseUrl = 'https://api.mapbox.com/geocoding/v5/mapbox.places'
  private cacheMaxAge = 24 * 60 * 60 * 1000 // 24 hours
  private defaultCountry = 'za' // South Africa ISO code

  constructor(apiKey: string) {
    if (!apiKey) {
      const error = createConvexError(
        ConvexErrorCodes.INTERNAL_ERROR,
        'Mapbox API key is required'
      )

      logConvexError(error, 'MapboxGeocodingService.Constructor', {
        additionalInfo: { service: 'mapbox', issue: 'missing_api_key' },
      })

      throw error
    }
    this.apiKey = apiKey
  }

  async geocodeAddress(address: string): Promise<GeocodingResult> {
    const requestId = generateRequestId()

    if (!address?.trim()) {
      const error = createConvexError(
        ConvexErrorCodes.VALIDATION_ERROR,
        'Address is required for geocoding',
        { address },
        requestId
      )

      logConvexError(error, 'MapboxGeocodingService.GeocodeAddress', {
        requestId,
        additionalInfo: { address, operation: 'forward_geocode' },
      })

      throw error
    }

    const cacheKey = `forward:${address.toLowerCase().trim()}`
    const cached = this.getCachedResult(cacheKey)
    if (cached) return cached

    try {
      const encodedAddress = encodeURIComponent(address.trim())
      const url = `${this.baseUrl}/${encodedAddress}.json?access_token=${this.apiKey}&limit=1&country=${this.defaultCountry}`

      const response = await fetch(url)
      if (!response.ok) {
        const error = createConvexError(
          ConvexErrorCodes.GEOCODING_ERROR,
          `Mapbox API error: ${response.status}`,
          { status: response.status, address },
          requestId
        )

        logConvexError(error, 'MapboxGeocodingService.GeocodeAddress', {
          requestId,
          additionalInfo: {
            address,
            status: response.status,
            operation: 'forward_geocode',
          },
        })

        throw error
      }

      const data = await response.json()
      if (!data.features || data.features.length === 0) {
        const error = createConvexError(
          ConvexErrorCodes.NOT_FOUND,
          'No geocoding results found',
          { address },
          requestId
        )

        logConvexError(error, 'MapboxGeocodingService.GeocodeAddress', {
          requestId,
          additionalInfo: { address, operation: 'forward_geocode' },
          level: 'warn',
        })

        throw error
      }

      const feature = data.features[0]
      const [lon, lat] = feature.center

      const result: GeocodingResult = {
        coordinates: [lon, lat],
        address: feature.place_name || address,
        confidence: this.mapConfidence(feature.relevance || 0),
        components: this.extractComponents(feature),
      }

      this.setCachedResult(cacheKey, result)
      return result
    } catch (error) {
      if (error instanceof ConvexError) {
        throw error // Re-throw ConvexError as-is
      }

      const convexError = createConvexError(
        ConvexErrorCodes.GEOCODING_ERROR,
        `Geocoding failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { address, originalError: error },
        requestId
      )

      logConvexError(convexError, 'MapboxGeocodingService.GeocodeAddress', {
        requestId,
        additionalInfo: { address, operation: 'forward_geocode' },
      })

      throw convexError
    }
  }

  async reverseGeocode(lat: number, lon: number): Promise<AddressResult> {
    const requestId = generateRequestId()

    if (!this.isValidCoordinate(lat, lon)) {
      const error = createConvexError(
        ConvexErrorCodes.VALIDATION_ERROR,
        'Invalid coordinates provided',
        { lat, lon },
        requestId
      )

      logConvexError(error, 'MapboxGeocodingService.ReverseGeocode', {
        requestId,
        additionalInfo: { lat, lon, operation: 'reverse_geocode' },
      })

      throw error
    }

    const cacheKey = `reverse:${lat.toFixed(5)},${lon.toFixed(5)}`
    const cached = this.getCachedResult(cacheKey)
    if (cached) return cached

    try {
      const url = `${this.baseUrl}/${lon},${lat}.json?access_token=${this.apiKey}&limit=1`

      const response = await fetch(url)
      if (!response.ok) {
        const error = createConvexError(
          ConvexErrorCodes.GEOCODING_ERROR,
          `Mapbox API error: ${response.status}`,
          { status: response.status, lat, lon },
          requestId
        )

        logConvexError(error, 'MapboxGeocodingService.ReverseGeocode', {
          requestId,
          additionalInfo: {
            lat,
            lon,
            status: response.status,
            operation: 'reverse_geocode',
          },
        })

        throw error
      }

      const data = await response.json()
      if (!data.features || data.features.length === 0) {
        const error = createConvexError(
          ConvexErrorCodes.NOT_FOUND,
          'No address found for coordinates',
          { lat, lon },
          requestId
        )

        logConvexError(error, 'MapboxGeocodingService.ReverseGeocode', {
          requestId,
          additionalInfo: { lat, lon, operation: 'reverse_geocode' },
          level: 'warn',
        })

        throw error
      }

      const feature = data.features[0]
      const result: AddressResult = {
        address: feature.place_name || `${lat}, ${lon}`,
        components: this.extractComponents(feature),
      }

      this.setCachedResult(cacheKey, result)
      return result
    } catch (error) {
      if (error instanceof ConvexError) {
        throw error // Re-throw ConvexError as-is
      }

      const convexError = createConvexError(
        ConvexErrorCodes.GEOCODING_ERROR,
        `Reverse geocoding failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { lat, lon, originalError: error },
        requestId
      )

      logConvexError(convexError, 'MapboxGeocodingService.ReverseGeocode', {
        requestId,
        additionalInfo: { lat, lon, operation: 'reverse_geocode' },
      })

      throw convexError
    }
  }

  validateCoordinates(
    lat: number,
    lon: number
  ): { isValid: boolean; errorMessage?: string; requestId?: string } {
    const requestId = generateRequestId()

    if (!this.isValidCoordinate(lat, lon)) {
      const errorMessage =
        'Coordinates must be valid: latitude (-90 to 90), longitude (-180 to 180)'

      logConvexError(
        new Error(errorMessage),
        'MapboxGeocodingService.ValidateCoordinates',
        {
          requestId,
          additionalInfo: { lat, lon, operation: 'validate_coordinates' },
          level: 'warn',
        }
      )

      return {
        isValid: false,
        errorMessage,
        requestId,
      }
    }

    return { isValid: true, requestId }
  }

  private getCachedResult(key: string): any | null {
    const cached = this.cache.get(key)
    if (!cached) return null

    if (Date.now() - cached.timestamp > this.cacheMaxAge) {
      this.cache.delete(key)
      return null
    }

    return cached.result
  }

  private setCachedResult(key: string, result: any): void {
    this.cache.set(key, { result, timestamp: Date.now() })
  }

  private isValidCoordinate(lat: number, lon: number): boolean {
    return (
      typeof lat === 'number' &&
      typeof lon === 'number' &&
      !isNaN(lat) &&
      !isNaN(lon) &&
      lat >= -90 &&
      lat <= 90 &&
      lon >= -180 &&
      lon <= 180
    )
  }

  private mapConfidence(relevance: number): GeocodingResult['confidence'] {
    if (relevance >= 0.9) return 'exact'
    if (relevance >= 0.7) return 'interpolated'
    return 'approximate'
  }

  private extractComponents(feature: any): GeocodingResult['components'] {
    const components: GeocodingResult['components'] = {}

    if (feature.context) {
      for (const context of feature.context) {
        const id = context.id || ''
        if (id.startsWith('postcode.')) components.postalCode = context.text
        else if (id.startsWith('place.')) components.city = context.text
        else if (id.startsWith('region.')) components.state = context.text
        else if (id.startsWith('country.')) components.country = context.text
      }
    }

    if (feature.address && feature.text) {
      components.street = `${feature.address} ${feature.text}`
    } else if (feature.text) {
      components.street = feature.text
    }

    return components
  }
}

// Singleton geocoding service instance with ConvexError support
let geocodingService: MapboxGeocodingService | null = null

export const getGeocodingService = (): MapboxGeocodingService => {
  if (!geocodingService) {
    const apiKey = process.env.VITE_MAPBOX_TOKEN
    if (!apiKey) {
      const error = createConvexError(
        ConvexErrorCodes.INTERNAL_ERROR,
        'VITE_MAPBOX_TOKEN environment variable is not set'
      )

      logConvexError(error, 'GeocodingService.Initialize', {
        additionalInfo: { service: 'mapbox', issue: 'missing_api_key' },
      })

      throw error
    }
    geocodingService = new MapboxGeocodingService(apiKey)
  }
  return geocodingService
}

/**
 * Enhanced VRP error handling patterns with ConvexError support and request ID tracking
 */
export const ConvexVRPErrorHandling = {
  project: {
    create: (error: unknown, requestId?: string, userId?: string) => {
      const message = parseConvexError(error, {
        context: ErrorContexts.PROJECT.CREATE,
        fallbackMessage: 'Failed to create project. Please try again.',
        requestId,
      })

      logConvexError(error, 'VRP.Project.Create', {
        requestId,
        userId,
        additionalInfo: { operation: 'create', entity: 'project' },
      })

      return message
    },

    update: (error: unknown, requestId?: string, userId?: string) => {
      const message = parseConvexError(error, {
        context: ErrorContexts.PROJECT.UPDATE,
        fallbackMessage: 'Failed to update project. Please try again.',
        requestId,
      })

      logConvexError(error, 'VRP.Project.Update', {
        requestId,
        userId,
        additionalInfo: { operation: 'update', entity: 'project' },
      })

      return message
    },

    delete: (error: unknown, requestId?: string, userId?: string) => {
      const message = parseConvexError(error, {
        context: ErrorContexts.PROJECT.DELETE,
        fallbackMessage: 'Failed to delete project. Please try again.',
        requestId,
      })

      logConvexError(error, 'VRP.Project.Delete', {
        requestId,
        userId,
        additionalInfo: { operation: 'delete', entity: 'project' },
      })

      return message
    },
  },

  table: {
    create: (error: unknown, requestId?: string, userId?: string) => {
      const message = parseConvexError(error, {
        context: ErrorContexts.TABLE.CREATE,
        fallbackMessage: 'Failed to create row. Please try again.',
        requestId,
      })

      logConvexError(error, 'VRP.Table.Create', {
        requestId,
        userId,
        additionalInfo: { operation: 'create', entity: 'table_row' },
      })

      return message
    },

    update: (error: unknown, requestId?: string, userId?: string) => {
      const message = parseConvexError(error, {
        context: ErrorContexts.TABLE.UPDATE,
        fallbackMessage: 'Failed to update row. Please try again.',
        requestId,
      })

      logConvexError(error, 'VRP.Table.Update', {
        requestId,
        userId,
        additionalInfo: { operation: 'update', entity: 'table_row' },
      })

      return message
    },

    delete: (error: unknown, requestId?: string, userId?: string) => {
      const message = parseConvexError(error, {
        context: ErrorContexts.TABLE.DELETE,
        fallbackMessage: 'Failed to delete row. Please try again.',
        requestId,
      })

      logConvexError(error, 'VRP.Table.Delete', {
        requestId,
        userId,
        additionalInfo: { operation: 'delete', entity: 'table_row' },
      })

      return message
    },

    bulkEdit: (error: unknown, requestId?: string, userId?: string) => {
      const message = parseConvexError(error, {
        context: ErrorContexts.TABLE.BULK_EDIT,
        fallbackMessage: 'Failed to bulk edit. Please try again.',
        requestId,
      })

      logConvexError(error, 'VRP.Table.BulkEdit', {
        requestId,
        userId,
        additionalInfo: { operation: 'bulk_edit', entity: 'table_rows' },
      })

      return message
    },

    bulkDelete: (error: unknown, requestId?: string, userId?: string) => {
      const message = parseConvexError(error, {
        context: ErrorContexts.TABLE.BULK_DELETE,
        fallbackMessage: 'Failed to bulk delete. Please try again.',
        requestId,
      })

      logConvexError(error, 'VRP.Table.BulkDelete', {
        requestId,
        userId,
        additionalInfo: { operation: 'bulk_delete', entity: 'table_rows' },
      })

      return message
    },
  },

  geocoding: {
    forward: (error: unknown, requestId?: string, userId?: string) => {
      const message = parseConvexError(error, {
        context: ErrorContexts.GEOCODING.FORWARD,
        fallbackMessage:
          'Failed to find coordinates for address. Please check the address and try again.',
        requestId,
      })

      logConvexError(error, 'VRP.Geocoding.Forward', {
        requestId,
        userId,
        additionalInfo: { operation: 'forward_geocode', service: 'mapbox' },
      })

      return message
    },

    reverse: (error: unknown, requestId?: string, userId?: string) => {
      const message = parseConvexError(error, {
        context: ErrorContexts.GEOCODING.REVERSE,
        fallbackMessage:
          'Failed to find address for coordinates. Please try again.',
        requestId,
      })

      logConvexError(error, 'VRP.Geocoding.Reverse', {
        requestId,
        userId,
        additionalInfo: { operation: 'reverse_geocode', service: 'mapbox' },
      })

      return message
    },

    validate: (error: unknown, requestId?: string, userId?: string) => {
      const message = parseConvexError(error, {
        context: ErrorContexts.GEOCODING.VALIDATE,
        fallbackMessage:
          'Invalid coordinates provided. Please check latitude and longitude values.',
        requestId,
      })

      logConvexError(error, 'VRP.Geocoding.Validate', {
        requestId,
        userId,
        additionalInfo: { operation: 'validate_coordinates' },
        level: 'warn',
      })

      return message
    },

    batch: (error: unknown, requestId?: string, userId?: string) => {
      const message = parseConvexError(error, {
        context: ErrorContexts.GEOCODING.BATCH,
        fallbackMessage: 'Failed to process batch geocoding. Please try again.',
        requestId,
      })

      logConvexError(error, 'VRP.Geocoding.Batch', {
        requestId,
        userId,
        additionalInfo: { operation: 'batch_geocode', service: 'mapbox' },
      })

      return message
    },
  },
}

// Backward compatibility - keep old name
export const VRPErrorHandling = ConvexVRPErrorHandling

// =============================================================================
// CONVEX ERROR DEBUGGING UTILITIES
// =============================================================================

/**
 * Get error logs from the global error tracker (development only)
 */
export function getConvexErrorLogs(): any[] {
  if (process.env.NODE_ENV !== 'development') {
    console.warn('Error logs are only available in development mode')
    return []
  }

  return (window as any).__convexErrorLogs || []
}

/**
 * Clear error logs from the global error tracker (development only)
 */
export function clearConvexErrorLogs(): void {
  if (process.env.NODE_ENV !== 'development') {
    console.warn('Error logs are only available in development mode')
    return
  }

  ;(window as any).__convexErrorLogs = []
}

/**
 * Enhanced location picker utility with ConvexError support
 */
export const ConvexLocationPickerUtils = {
  /**
   * Create a map click handler for location selection with error handling
   */
  createLocationHandler: (
    onLocationSelect: (coords: [number, number], address?: string) => void
  ) => {
    return async (event: any) => {
      const { lng, lat } = event.lngLat || event
      const requestId = generateRequestId()

      try {
        const geocodingService = getGeocodingService()
        const result = await geocodingService.reverseGeocode(lat, lng)
        onLocationSelect([lng, lat], result.address)
      } catch (error) {
        logConvexError(error, 'LocationPicker.ReverseGeocode', {
          requestId,
          additionalInfo: { lat, lng, operation: 'map_click' },
          level: 'warn',
        })
        onLocationSelect([lng, lat], `${lat.toFixed(6)}, ${lng.toFixed(6)}`)
      }
    }
  },

  /**
   * Validate coordinates for location creation with ConvexError support
   */
  validateCoordinates: (lat: number, lng: number): boolean => {
    const isValid = lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180

    if (!isValid) {
      logConvexError(
        new Error(`Invalid coordinates: lat=${lat}, lng=${lng}`),
        'LocationPicker.ValidateCoordinates',
        {
          additionalInfo: { lat, lng },
          level: 'warn',
        }
      )
    }

    return isValid
  },

  /**
   * Format coordinates for display
   */
  formatCoordinates: (lat: number, lng: number): string => {
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`
  },
}

// Backward compatibility - keep old name
export const LocationPickerUtils = ConvexLocationPickerUtils
